import React from 'react';
import { DataGridPremium } from '@mui/x-data-grid-premium';
import { Box, IconButton, Typography, Chip } from '@mui/material';
import TimelineRoundedIcon from '@mui/icons-material/TimelineRounded';
import { daDK } from '@mui/x-data-grid/locales';
import { useTheme } from '@mui/material/styles';
import ModelTrainingRoundedIcon from '@mui/icons-material/ModelTrainingRounded';
import { useTranslation } from 'react-i18next';

const formatDate = (date) => {
  const formatter = new Intl.DateTimeFormat('da-DK', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });

  const dateObj = new Date(date);
  const isValid = (d) => d instanceof Date && !isNaN(d.getTime());
  const formattedDate = isValid(dateObj) ? formatter.format(dateObj) : '';
  return formattedDate;
};

const formatDateRange = (start, end) => {
  const formatter = new Intl.DateTimeFormat('da-DK', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });

  const startDate = start ? new Date(start) : null;
  const endDate = end ? new Date(end) : null;

  const isValid = (d) => d instanceof Date && !isNaN(d.getTime());

  const formattedStart = isValid(startDate) ? formatter.format(startDate) : '';
  const formattedEnd = isValid(endDate) ? formatter.format(endDate) : '';

  return `${formattedStart} - ${formattedEnd}`;
};

const InitiatedInitiativeDataGrid = ({
  enrichedInitiatives,
  handleSelectInitiative,
  handleEditInitiative,
  isLoading,
}) => {
  const theme = useTheme();
  const { t } = useTranslation('energyxplorer-initiatives-tab');

  const handleRowClick = (event, row) => {
    handleEditInitiative(row);
  };

  const columns = [
    {
      field: 'name',
      headerName: t('columns.name.header'),
      flex: 2,
      minWidth: 200,
      renderCell: (params) => (
        <Box display="flex" alignItems="center" width="100%" height="100%">
          <ModelTrainingRoundedIcon
            sx={(theme) => ({
              mr: 1,
              color: params.row.advancedSavingsAvailable
                ? 'success.main'
                : theme.palette.mode === 'light'
                  ? 'lightgray'
                  : 'gray',
            })}
          />
          <Typography>{params.value}</Typography>
        </Box>
      ),
    },
    {
      field: 'createdDate',
      headerName: t('columns.created.header'),
      flex: 1,
      minWidth: 150,
      renderCell: (params) => {
        const date = new Date(params.value);
        return (
          <Box display="flex" alignItems="center" width="100%" height="100%">
            <Typography>{formatDate(date)}</Typography>
          </Box>
        );
      },
    },
    {
      field: 'changedDate',
      headerName: t('columns.changed.header'),
      flex: 1,
      minWidth: 150,
      renderCell: (params) => {
        const date = new Date(params.value);
        return (
          <Box display="flex" alignItems="center" width="100%" height="100%">
            <Typography>{formatDate(date)}</Typography>
          </Box>
        );
      },
    },
    {
      field: 'baselinePeriod',
      headerName: t('columns.baseline_period.header'),
      flex: 1,
      renderCell: (params) => (
        <Box display="flex" alignItems="center" width="100%" height="100%">
          <Typography>
            {formatDateRange(params.row.baselineStart, params.row.baselineEnd)}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'comparisonPeriod',
      headerName: t('columns.comparison_period.header'),
      flex: 1,
      renderCell: (params) => (
        <Box display="flex" alignItems="center" width="100%" height="100%">
          <Typography>
            {formatDateRange(
              params.row.comparisonStart,
              params.row.comparisonEnd,
            )}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'actions',
      headerName: '',
      flex: 1,
      minWidth: 120,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box display="flex" alignItems="center">
          <div onClick={(e) => e.stopPropagation()}>
            <IconButton
              sx={{ mr: 2, borderRadius: 1 }}
              disabled={true} //for now, until we implement the feature
              onClick={(e) => {
                e.stopPropagation();
                handleSelectInitiative(params.row);
              }}
            >
              <TimelineRoundedIcon />
            </IconButton>
          </div>
        </Box>
      ),
    },
  ];

  const rows = enrichedInitiatives.map((initiative) => ({
    id: initiative.energyInitiativeId,
    name: initiative.name,
    description: initiative.description,
    baselineStart: initiative.baselineStart,
    baselineEnd: initiative.baselineEnd,
    comparisonStart: initiative.comparisonStart,
    comparisonEnd: initiative.comparisonEnd,
    nodes: initiative.energyTreeNodes,
    state: initiative.state,
    createdDate: initiative.createdDate,
    changedDate: initiative.changedDate,
    completedDate: initiative.completedDate,
    data: initiative.energySavings,
    actualCO2eEmission:
      initiative.energySavings?.BasicEnergySavings?.CO2eSavingsSummary
        ?.ActualCO2eEmission,
    co2eSavings:
      initiative.energySavings?.BasicEnergySavings?.CO2eSavingsSummary
        ?.CO2eSavings,
    co2eSavingsPercentage:
      initiative.energySavings?.BasicEnergySavings?.CO2eSavingsSummary
        ?.CO2eSavingsPercentage,
    averageBaselineEnergyUsage:
      initiative.energySavings?.BasicEnergySavings?.EnergySavingsSummary
        ?.AverageBaselineEnergyUsage,
    averageComparisonEnergyUsage:
      initiative.energySavings?.BasicEnergySavings?.EnergySavingsSummary
        ?.AverageComparisonEnergyUsage,
    energySavings:
      initiative.energySavings?.BasicEnergySavings?.EnergySavingsSummary
        ?.EnergySavings,
    energySavingsPercentage:
      initiative.energySavings?.BasicEnergySavings?.EnergySavingsSummary
        ?.EnergySavingsPercentage,
    main: initiative,
  }));

  return (
    <Box sx={{ width: '100%' }}>
      <Box>
        <DataGridPremium
          localeText={daDK.components.MuiDataGrid.defaultProps.localeText}
          rows={rows}
          columns={columns}
          hideFooter
          pagination
          disableSelectionOnClick
          pageSizeOptions={[10, 20, 50]}
          initialState={{
            pagination: {
              paginationModel: { page: 0, pageSize: 10 },
            },
          }}
          onRowClick={(params, event) => {
            handleRowClick(event, params.row);
          }}
          loading={isLoading}
          slotProps={{
            loadingOverlay: {
              variant: 'linear-progress',
              noRowsVariant: 'skeleton',
            },
          }}
          sx={{
            // border: 'none',
            border: (theme) => `1px solid ${theme.palette.divider}`,
            borderRadius: 2,
            '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within, & .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within':
              {
                outline: 'none',
              },
            '& .MuiDataGrid-columnHeaderTitle': {
              fontWeight: 'bold',
            },
            '& .MuiDataGrid-footerContainer': {
              backgroundColor: (theme) =>
                theme.palette.mode === 'light' ? '#fff' : '#121212',
              borderTop: '1px solid', // Optional: Adjust border color
              borderColor: 'divider',
            },
            '& .MuiTablePagination-root': {
              backgroundColor: (theme) =>
                theme.palette.mode === 'light' ? '#fff' : '#121212',
              borderTop: '1px solid',
              borderColor: 'divider',
            },
            '& .MuiDatagrid-root': {
              border: 0,
            },
            '.MuiDataGrid-withBorderColor': {
              border: 'none !important',
            },
            '& .MuiDatagrid-footerContainer': {
              border: 0,
            },
            '& .MuiDatagrid-bottomContainer': {
              border: 0,
            },
            '& .MuiDataGrid-main': {
              backgroundColor: (theme) =>
                theme.palette.mode === 'light' ? '#fff' : '#121212',
            },
            '& .MuiDataGrid-columnsContainer': {
              backgroundColor: 'blue',
            },
            '& .MuiDataGrid-columnHeaders': {
              borderBottom: '1px solid', // Optional: Add a border
              borderColor: 'divider',
            },
            '& .selected-row': {
              backgroundColor: (theme) => theme.palette.action.selected,
              '&:hover': {
                backgroundColor: (theme) => theme.palette.action.selected,
              },
            },
            '& .MuiDataGrid-scrollbar': {
              overflow: 'hidden !important',
            },
          }}
        />
      </Box>
    </Box>
  );
};

export default InitiatedInitiativeDataGrid;
